import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import {
  CAPABILITIES,
  capabilityForTemplate,
} from '../../modules/capabilities/capabilities';
import { PlannedServicePage } from './PlannedServicePage';
import { ServiceShell } from './ServiceShell';
import { ServicesPage } from './ServicesPage';
import { Typography } from '@material-ui/core';
import { Content } from '@backstage/core-components';
import { canRunAnyWorkflow } from '@internal/datasphere-personas';
import { usePersonaSession } from '@internal/datasphere-personas/react';

/** The Services list, for personas that can run at least one service. */
const ServicesIndex = () => {
  const persona = usePersonaSession();
  if (!persona.loading && !canRunAnyWorkflow(persona.capabilities)) {
    return (
      <Content>
        <Typography variant="body1">
          Services are not available for your role.
        </Typography>
      </Content>
    );
  }
  return <ServicesPage />;
};

/** A roadmap service that is not built yet, inside the service frame. */
const PlannedRoute = () => {
  const { capabilityId } = useParams();
  const capability = CAPABILITIES.find(
    c => c.id === capabilityId && c.availability !== 'live',
  );
  if (!capability) return <Navigate to=".." replace />;
  return (
    <ServiceShell current={capability}>
      <PlannedServicePage capability={capability} />
    </ServiceShell>
  );
};

/** The stock template wizard, inside the same frame as planned services. */
const WizardRoute = ({ wizard }: { wizard: ReactNode }) => {
  // `*` is `:namespace/:templateName`, plus nothing else on this sub-page.
  const templateName = (useParams()['*'] ?? '').split('/')[1] ?? '';
  return (
    <ServiceShell
      current={capabilityForTemplate(templateName)}
      fallbackTitle={templateName}
      templateName={templateName}
    >
      {wizard}
    </ServiceShell>
  );
};

/**
 * Shows our Services list at the sub-page root, planned roadmap services at
 * `planned/:capabilityId`, and hands every other path (the template wizard at
 * `:namespace/:templateName`) to the stock scaffolder sub-page element, framed
 * by the service rail.
 */
export const ServicesSubPage = ({ wizard }: { wizard: ReactNode }) => (
  <Routes>
    <Route index element={<ServicesIndex />} />
    <Route path="planned/:capabilityId" element={<PlannedRoute />} />
    <Route path="*" element={<WizardRoute wizard={wizard} />} />
  </Routes>
);
